import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { AlertCircleIcon } from "../../Icons";
import {
  MODAL_PANEL,
  MODAL_TITLE,
  BTN_SECONDARY,
  BTN_DANGER,
  BTN_PRIMARY,
} from "../../ui/pageChrome";

const TONES = {
  danger: {
    iconWrap: "bg-red-50 text-red-500 border border-red-100",
    confirm: BTN_DANGER,
  },
  primary: {
    iconWrap: "bg-indigo-50 text-indigo-500 border border-indigo-100",
    confirm: BTN_PRIMARY,
  },
};

/**
 * Styled replacement for `window.confirm` on destructive actions.
 *
 * Rendered in a portal at z-60 so it stacks above the module's z-50 modals —
 * several delete actions are triggered from inside an already-open dialog.
 */
const ConfirmDialog = ({
  open,
  title,
  message,
  confirmText = "Delete",
  cancelText = "Cancel",
  tone = "danger",
  onConfirm,
  onCancel,
}) => {
  const cancelRef = useRef(null);
  const restoreFocusRef = useRef(null);

  // Callers pass fresh closures each render; keeping them in a ref lets the
  // effect below key off `open` alone, so focus is not stolen on re-render.
  const onCancelRef = useRef(onCancel);
  useEffect(() => {
    onCancelRef.current = onCancel;
  });

  useEffect(() => {
    if (!open) return undefined;

    restoreFocusRef.current = document.activeElement;
    // Focus lands on Cancel so a stray Enter can never confirm a delete.
    cancelRef.current?.focus();

    // Capture phase + stopPropagation: several deletes are triggered from
    // inside a dialog that also closes on Escape, and only this top-most
    // layer should react to the key.
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onCancelRef.current?.();
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      restoreFocusRef.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  const toneStyle = TONES[tone] || TONES.danger;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-[1px]"
      onClick={() => onCancel?.()}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby={message ? "confirm-dialog-message" : undefined}
        className={`${MODAL_PANEL} max-w-sm`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start gap-3 px-4 pt-4">
          <span
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${toneStyle.iconWrap}`}
          >
            <AlertCircleIcon size={17} />
          </span>
          <div className="min-w-0 pt-0.5">
            <h3 id="confirm-dialog-title" className={MODAL_TITLE}>
              {title}
            </h3>
            {message && (
              <p
                id="confirm-dialog-message"
                className="mt-1 text-[12px] leading-relaxed text-slate-500"
              >
                {message}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 mt-3 border-t border-slate-100">
          <button
            ref={cancelRef}
            type="button"
            onClick={() => onCancel?.()}
            className={BTN_SECONDARY}
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={() => onConfirm?.()}
            className={toneStyle.confirm}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

ConfirmDialog.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.string,
  message: PropTypes.string,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  tone: PropTypes.oneOf(["danger", "primary"]),
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
};

export default ConfirmDialog;
